/**
 * The schema-driven form of Cài đặt › Module (ADR-025 §8): which control each field kind gets,
 * when a field is required, the checks the form makes before the server's (the server's are
 * modules/kit/host/src/validate.rs), and the values it sends. Plain TypeScript without React, so
 * node tests load it (tests/module-labels.test.mjs).
 */

import type { BilingualText, ModuleField, ModuleFieldKind, ModuleFieldType, ModuleVersion } from '../../api/areas/modules.ts';

/** The control drawn for a field (ADR-025 §8 table). */
export type FieldControl = 'input' | 'secret' | 'select' | 'checkboxes' | 'url' | 'number' | 'switch' | 'time' | 'certificate' | 'upload';

export const FIELD_CONTROLS: Readonly<Record<ModuleFieldType, FieldControl>> = {
  text: 'input',
  email: 'input',
  phone: 'input',
  path: 'input',
  secret: 'secret',
  select: 'select',
  multi_select: 'checkboxes',
  url: 'url',
  integer: 'number',
  boolean: 'switch',
  time_of_day: 'time',
  certificate: 'certificate',
  file: 'upload',
};

/** Write-only kinds: sent as secrets (KEEP / REFERENCE / VALUE), never read back. */
export function isWriteOnly(kind: ModuleFieldKind): boolean {
  return kind.type === 'secret' || kind.type === 'certificate' || kind.type === 'file';
}

/** A label in the reader's language. */
export function pick(text: BilingualText | null | undefined, lang: string): string {
  if (!text) return '';
  return lang === 'en' ? text.en || text.vi : text.vi || text.en;
}

/** The value of a field while it is edited: text (numbers too), a switch, or a list of choices. */
export type FormValue = string | boolean | string[];

/** The value shown in the form: the version's, else the default, else empty. */
export function initialValue(field: ModuleField, version: Pick<ModuleVersion, 'values'> | null): FormValue {
  const stored = version?.values[field.key];
  const v = stored === undefined || stored === null ? field.default : stored;
  switch (field.kind.type) {
    case 'boolean':
      return v === true;
    case 'multi_select':
      return Array.isArray(v) ? v.map(String) : [];
    default:
      return v === undefined || v === null ? '' : String(v);
  }
}

/** The stored value read for `Required::If` (`equals` is compared with its text form). */
function asText(v: FormValue | unknown): string | null {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}

/** Whether a value is needed (modules/kit/sdk `Field::is_required`); a blank value takes the default. */
export function isRequired(field: ModuleField, enabled: boolean, values: Readonly<Record<string, FormValue>>, fields: readonly ModuleField[]): boolean {
  const r = field.required;
  switch (r.when) {
    case 'always':
      return true;
    case 'when_enabled':
      return enabled;
    case 'never':
      return false;
    case 'if': {
      const other = fields.find((f) => f.key === r.field);
      const raw = values[r.field];
      const v = asText(raw) ?? asText(other?.default ?? null);
      return v === r.equals;
    }
  }
}

/** Whether the field has no value of its own (the server then uses its default). */
export function isBlank(value: FormValue | undefined): boolean {
  if (value === undefined) return true;
  if (typeof value === 'boolean') return false;
  if (Array.isArray(value)) return false;
  return value.trim() === '';
}

const EMAIL = /^[^\s@]{1,64}@[^\s@]+\.[^\s@.]+$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const PATH_CHARS = /^\/[A-Za-z0-9\-._~/%!$&'()*+,;=:@]*$/;

/** The form's own check of one value (null: fine, or left to the server). Codes: `modules:form.errors.*`. */
export function checkValue(field: ModuleField, value: FormValue, production: boolean): FormErrorCode | null {
  if (isBlank(value)) return null;
  const k = field.kind;
  const s = typeof value === 'string' ? value.trim() : '';
  switch (k.type) {
    case 'text':
      if (s.length > k.max_len) return 'TOO_LONG';
      if (k.pattern) {
        try {
          if (!new RegExp(`^(?:${k.pattern})$`, 'u').test(s)) return 'PATTERN';
        } catch {
          return null;
        }
      }
      return null;
    case 'select':
      return k.options.length && !k.options.some((o) => o.value === s) ? 'NOT_IN_OPTIONS' : null;
    case 'multi_select':
      return Array.isArray(value) && value.some((v) => !k.options.some((o) => o.value === v)) ? 'NOT_IN_OPTIONS' : null;
    case 'url': {
      let u: URL;
      try {
        u = new URL(s);
      } catch {
        return 'INVALID_URL';
      }
      if (u.protocol !== 'https:' && u.protocol !== 'http:') return 'INVALID_URL';
      if ((k.https_only || production) && u.protocol !== 'https:') return 'HTTPS_REQUIRED';
      return null;
    }
    case 'path':
      return PATH_CHARS.test(s) && s.length <= 512 && !s.split('/').includes('..') ? null : 'INVALID_PATH';
    case 'integer': {
      if (!/^-?\d+$/.test(s)) return 'NOT_A_NUMBER';
      const n = Number(s);
      return n < k.min || n > k.max ? 'OUT_OF_RANGE' : null;
    }
    case 'time_of_day':
      return TIME.test(s) ? null : 'INVALID_TIME';
    case 'email':
      return EMAIL.test(s) && s.length <= 254 ? null : 'INVALID_EMAIL';
    case 'phone': {
      const digits = s.replace(/^\+/, '').replace(/[ .-]/g, '');
      return /^84\d{9,10}$/.test(digits) ? null : 'INVALID_PHONE';
    }
    case 'boolean':
    case 'secret':
    case 'certificate':
    case 'file':
      return null;
  }
}

export const FORM_ERROR_CODES = [
  'REQUIRED',
  'TOO_LONG',
  'PATTERN',
  'NOT_IN_OPTIONS',
  'HTTPS_REQUIRED',
  'INVALID_URL',
  'OUT_OF_RANGE',
  'INVALID_TIME',
  'INVALID_EMAIL',
  'INVALID_PHONE',
  'INVALID_PATH',
  'NOT_A_NUMBER',
] as const;
export type FormErrorCode = (typeof FORM_ERROR_CODES)[number];

/** The value sent for a field: blank text is null (the server applies the default). */
export function payloadValue(field: ModuleField, value: FormValue): unknown {
  switch (field.kind.type) {
    case 'boolean':
      return value === true;
    case 'multi_select':
      return Array.isArray(value) ? value : [];
    case 'integer': {
      const s = typeof value === 'string' ? value.trim() : '';
      return s === '' ? null : Number(s);
    }
    default: {
      const s = typeof value === 'string' ? value.trim() : '';
      return s === '' ? null : s;
    }
  }
}

/** Fields shown together: by `group` (in the order they first appear), the advanced ones last. */
export function groupFields(fields: readonly ModuleField[]): { group: string | null; advanced: boolean; fields: ModuleField[] }[] {
  const out: { group: string | null; advanced: boolean; fields: ModuleField[] }[] = [];
  for (const advanced of [false, true]) {
    for (const f of fields.filter((x) => x.advanced === advanced)) {
      const g = out.find((x) => x.group === f.group && x.advanced === advanced);
      if (g) g.fields.push(f);
      else out.push({ group: f.group, advanced, fields: [f] });
    }
  }
  return out;
}

/** "64 KB", "1.5 MB": the size of a file or a body. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round((n / 1024) * 10) / 10} KB`;
  return `${Math.round((n / (1024 * 1024)) * 10) / 10} MB`;
}

/** Text files are sent as they are; any other file as base64. */
export function isTextFile(mime: readonly string[], file: { type: string; name: string }): boolean {
  const types = [...mime, file.type];
  return types.some((m) => m.startsWith('text/') || /pem|json|xml|x-x509/.test(m)) || /\.(pem|crt|cer|txt|json|xml)$/i.test(file.name);
}
